import { useState } from "react";
import { Lock, Sparkles, FileText, FileSignature, AlertOctagon, FileEdit, ShieldCheck, Loader2, ArrowRight, Check, CreditCard } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createMolliePayment } from "@/lib/api";
import { toast } from "sonner";

const DOC_PREVIEWS = [
  { title: "Opzegbrief", icon: FileSignature, color: "blue", desc: "Direct opzeggen van je energiecontract" },
  { title: "Klachtbrief", icon: AlertOctagon, color: "amber", desc: "Officiële klacht aan je leverancier" },
  { title: "Bezwaarbrief factuur", icon: FileText, color: "red", desc: "Bezwaar tegen onjuiste facturen" },
  { title: "Meterstand correctie", icon: FileEdit, color: "emerald", desc: "Verzoek tot correcte meterstanden" },
];

const COLOR_BG = {
  blue: "bg-blue-50 text-blue-700 border-blue-100",
  amber: "bg-amber-50 text-amber-700 border-amber-100",
  red: "bg-red-50 text-red-700 border-red-100",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
};

export default function PaywallSection({ analysisId, teaser, hasReportUnlocked = false }) {
  const [email, setEmail] = useState("");
  const [loadingPkg, setLoadingPkg] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("ideal"); // "ideal" | "creditcard" | "any"

  const start = async (pkg) => {
    if (!email.trim() || !email.includes("@")) {
      toast.error("Vul een geldig e-mailadres in om verder te gaan.");
      return;
    }
    setLoadingPkg(pkg);
    try {
      const origin = window.location.origin;
      const result = await createMolliePayment({
        analysis_id: analysisId,
        package: pkg,
        email: email.trim(),
        origin_url: origin,
        method: paymentMethod === "any" ? null : paymentMethod,
      });
      if (result.url) {
        window.location.href = result.url;
      } else {
        throw new Error("Geen checkout URL ontvangen");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Er ging iets mis";
      // Friendlier copy for the most common Mollie activation error
      if (msg.includes("not activated")) {
        toast.error(
          `${paymentMethod === "creditcard" ? "Creditcard" : "Deze methode"} is nog in beoordeling bij Mollie. Kies tijdelijk iDEAL of "Andere methode".`,
          { duration: 6000 }
        );
      } else {
        toast.error(`Betaling starten mislukt: ${msg}`);
      }
      setLoadingPkg(null);
    }
  };

  return (
    <section className="mt-10 space-y-10" data-testid="paywall-section">
      {/* Teaser banner */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="teaser-banner">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
              <Sparkles className="h-3.5 w-3.5" />
              Analyse compleet
            </div>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              We hebben <span className="text-blue-700">{teaser?.findings_count || 0} bevindingen</span> in je contract gevonden
            </h2>
            <p className="mt-2 text-slate-600">
              Plus 4 kant-en-klare brieven die je kan downloaden zodra je ontgrendelt.
            </p>
          </div>
          {teaser?.possible_savings_max > 0 && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-5 py-4 text-center min-w-[200px]" data-testid="teaser-savings-preview">
              <div className="text-xs uppercase tracking-wider text-emerald-700 font-bold">Mogelijke besparing</div>
              <div className="font-heading text-2xl font-bold text-emerald-700 mt-1">
                €{teaser.possible_savings_min}–€{teaser.possible_savings_max}
              </div>
              <div className="text-[11px] text-emerald-700/70">per jaar geschat</div>
            </div>
          )}
        </div>
      </div>

      {/* Document preview cards (locked) */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">Inbegrepen in de brievenbundel</span>
            <h3 className="mt-2 font-heading text-xl sm:text-2xl font-bold text-slate-900">
              4 kant-en-klare PDF brieven — direct downloadbaar
            </h3>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {DOC_PREVIEWS.map((d, i) => (
            <div
              key={i}
              className="relative bg-white border border-slate-200 rounded-2xl p-5 hover:border-blue-200 transition-all"
              data-testid={`doc-preview-${i}`}
            >
              <div className={`h-11 w-11 rounded-xl border grid place-items-center mb-3 ${COLOR_BG[d.color]}`}>
                <d.icon className="h-5 w-5" />
              </div>
              <div className="font-heading font-semibold text-slate-900">{d.title}</div>
              <div className="text-sm text-slate-500 mt-1 leading-relaxed">{d.desc}</div>
              <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-slate-400">
                <Lock className="h-3 w-3" />
                Vergrendeld
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing options */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 rounded-3xl p-6 sm:p-10 text-white shadow-2xl" data-testid="paywall-pricing">
        <div className="text-center mb-8">
          <h3 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            Ontgrendel je rapport
          </h3>
          <p className="mt-2 text-slate-300 text-sm sm:text-base max-w-xl mx-auto">
            Eenmalige betaling. Geen abonnement. Direct toegang tot alles.
          </p>
        </div>

        {/* Email field */}
        <div className="max-w-md mx-auto mb-5">
          <Label htmlFor="paywall-email" className="text-sm font-semibold text-slate-200 mb-1.5 block">
            Je e-mailadres
          </Label>
          <Input
            id="paywall-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jij@voorbeeld.nl"
            className="bg-white text-slate-900"
            data-testid="paywall-email-input"
          />
          <p className="mt-1.5 text-[11px] text-slate-400 text-center">
            Voor je betaalbevestiging — geen spam, geen nieuwsbrief.
          </p>
        </div>

        {/* Payment method picker — iDEAL prominent for NL market */}
        <div className="max-w-md mx-auto mb-7">
          <div className="text-sm font-semibold text-slate-200 mb-2 text-center">Betaalmethode</div>
          <div className="grid grid-cols-3 gap-2" data-testid="payment-method-picker">
            <button
              type="button"
              onClick={() => setPaymentMethod("ideal")}
              className={`flex flex-col items-center justify-center gap-1.5 rounded-xl px-2 py-3 text-xs font-semibold transition-all border-2 ${
                paymentMethod === "ideal"
                  ? "bg-white text-slate-900 border-amber-400 shadow-lg"
                  : "bg-white/[0.04] text-slate-200 border-white/10 hover:border-white/30"
              }`}
              data-testid="payment-method-ideal"
            >
              <span className="text-base font-bold tracking-tight">
                <span className="text-pink-600">i</span><span className={paymentMethod === "ideal" ? "text-slate-900" : "text-white"}>DEAL</span>
              </span>
              <span className="text-[10px] font-medium opacity-80">Via je bank</span>
            </button>
            <button
              type="button"
              onClick={() => setPaymentMethod("creditcard")}
              className={`flex flex-col items-center justify-center gap-1.5 rounded-xl px-2 py-3 text-xs font-semibold transition-all border-2 ${
                paymentMethod === "creditcard"
                  ? "bg-white text-slate-900 border-amber-400 shadow-lg"
                  : "bg-white/[0.04] text-slate-200 border-white/10 hover:border-white/30"
              }`}
              data-testid="payment-method-card"
            >
              <CreditCard className="h-5 w-5" />
              <span className="text-[10px] font-medium opacity-80">Creditcard</span>
            </button>
            <button
              type="button"
              onClick={() => setPaymentMethod("any")}
              className={`flex flex-col items-center justify-center gap-1.5 rounded-xl px-2 py-3 text-xs font-semibold transition-all border-2 ${
                paymentMethod === "any"
                  ? "bg-white text-slate-900 border-amber-400 shadow-lg"
                  : "bg-white/[0.04] text-slate-200 border-white/10 hover:border-white/30"
              }`}
              data-testid="payment-method-any"
            >
              <Sparkles className="h-5 w-5" />
              <span className="text-[10px] font-medium opacity-80">Andere</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          {/* Report only */}
          <div
            className="bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-2xl p-6 flex flex-col"
            data-testid="package-card-report"
          >
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300">Rapport</div>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span className="font-heading text-4xl font-bold">€6,95</span>
              <span className="text-slate-400 text-sm">eenmalig</span>
            </div>
            <p className="mt-2 text-slate-300 text-sm">Volledige analyse + leveranciersadvies. Brieven zijn los te kopen.</p>
            <ul className="mt-5 space-y-2 text-sm flex-1">
              {["Volledig contractrapport", "Alle gevonden risico's & tips", "Top 3 leveranciers-advies", "Contractdetails & kleine letters"].map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-200">
                  <Check className="h-4 w-4 text-blue-300 mt-0.5 flex-shrink-0" />
                  {b}
                </li>
              ))}
              <li className="flex items-start gap-2 text-slate-500 line-through">
                <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                4 PDF brieven download
              </li>
            </ul>
            <button
              onClick={() => start("report")}
              disabled={loadingPkg !== null || hasReportUnlocked}
              className="mt-6 inline-flex items-center justify-center gap-2 bg-white text-slate-900 hover:bg-slate-100 font-semibold rounded-lg px-5 py-3 transition-all disabled:opacity-50"
              data-testid="package-report-btn"
            >
              {loadingPkg === "report" ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Doorgaan...</>
              ) : (
                <>{hasReportUnlocked ? "Al ontgrendeld" : "Kies rapport · €6,95"}<ArrowRight className="h-4 w-4" /></>
              )}
            </button>
          </div>

          {/* Bundle (recommended) */}
          <div
            className="relative bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-6 flex flex-col ring-2 ring-blue-400/40 shadow-xl"
            data-testid="package-card-bundle"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-slate-900 text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
              Aanrader
            </div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-100">Rapport + Brievenbundel</div>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span className="font-heading text-4xl font-bold">€19,95</span>
              <span className="text-blue-200 text-sm">eenmalig</span>
            </div>
            <p className="mt-2 text-blue-100 text-sm">Alles uit het rapport plus alle 4 brieven direct downloadbaar.</p>
            <ul className="mt-5 space-y-2 text-sm flex-1">
              {[
                "Alles uit het Rapport-pakket",
                "Opzegbrief (PDF)",
                "Klachtbrief leverancier (PDF)",
                "Bezwaarbrief factuur (PDF)",
                "Verzoek correctie meterstand (PDF)",
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-white">
                  <Check className="h-4 w-4 text-emerald-300 mt-0.5 flex-shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
            <button
              onClick={() => start("bundle")}
              disabled={loadingPkg !== null}
              className="mt-6 inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold rounded-lg px-5 py-3 transition-all disabled:opacity-50"
              data-testid="package-bundle-btn"
            >
              {loadingPkg === "bundle" ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Doorgaan...</>
              ) : (
                <>Kies bundel · €19,95<ArrowRight className="h-4 w-4" /></>
              )}
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-400">
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" />
            Veilig betalen via Mollie · iDEAL, creditcard & meer
          </span>
          <span className="hidden sm:inline">·</span>
          <span>Geen abonnement</span>
          <span className="hidden sm:inline">·</span>
          <span>Direct toegang na betaling</span>
        </div>
      </div>
    </section>
  );
}
